import { coreApi, createVenueSpecificClient } from '$api'
import {
  PayClassification,
  PayClassificationRequest,
  transformPayClassificationOnGet,
} from '$models'
import {
  getActiveCompanySettings,
  singletonQueryClient,
  getCompanyId,
} from '$utils'
import sortBy from 'lodash/sortBy'

enum QueryKeys {
  GetPayClassifications = 'get-pay-classifications',
  GetPayClassification = 'get-pay-classification',
}

/**
 * Get all Pay Classifications
 * @param includeDeleted - include the deleted pay classifications
 * @param venueId - optional venue ID to fetch pay classifications for specific venue
 * @returns Promise<PayClassification[]>
 */
export const getAll = async (
  includeDeleted: boolean = false,
  venueId?: string
): Promise<PayClassification[]> => {
  const { hasTimePermission, hasPayrollPermission } = getActiveCompanySettings()

  if (!hasTimePermission && !hasPayrollPermission) return []

  const client = venueId ? createVenueSpecificClient(venueId) : coreApi
  const companyId = venueId || getCompanyId()

  const response = await singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.GetPayClassifications, companyId, includeDeleted],
    queryFn: async ({ signal }) => {
      return await client.getPayClassifications(includeDeleted, signal)
    },
  })

  const result = response.map((pc) => transformPayClassificationOnGet(pc))
  return sortBy<PayClassification>(result, 'name')
}

/**
 * Get a Pay Classification by ID
 * @param payClassificationId the ID of the pay classification to get
 * @returns the pay classificaion with the given ID
 */
export const getPayClassification = async (
  payClassificationId: string
): Promise<PayClassification | null> => {
  const { hasTimePermission, hasPayrollPermission } = getActiveCompanySettings()

  if (!hasTimePermission && !hasPayrollPermission) return null

  const companyId = getCompanyId()

  const response = await singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.GetPayClassification, companyId, payClassificationId],
    queryFn: async () => {
      return await coreApi.getPayClassification(payClassificationId)
    },
  })
  return transformPayClassificationOnGet(response)
}

/**
 * Creates/adds a new pay classification
 * @param payClassification the pay classification to add/create
 * @returns the newly created pay classification
 */
export const addPayClassification = async (
  payClassification: PayClassification
): Promise<PayClassification | null> => {
  const { hasTimePermission, hasPayrollPermission } = getActiveCompanySettings()

  if (!hasTimePermission && !hasPayrollPermission) return null

  const newPayClassification = await coreApi.postPayClassification(
    payClassification as PayClassificationRequest
  )
  return transformPayClassificationOnGet(newPayClassification)
}

/**
 * Updates an existing pay classification
 * @param payClassification the pay classification to update
 * @returns the updated pay classification
 */
export const updatePayClassification = async (
  payClassification: PayClassification
): Promise<PayClassification | null> => {
  const { hasTimePermission, hasPayrollPermission } = getActiveCompanySettings()

  if (!hasTimePermission && !hasPayrollPermission) return null

  const updatedPayClassification = await coreApi.putPayClassification(
    payClassification.id!,
    payClassification as PayClassificationRequest
  )
  return transformPayClassificationOnGet(updatedPayClassification)
}
